import { ReactNode } from 'react';
import clsx from 'clsx';

import styles from './Badge.module.css';

export type Tone = 'neutral' | 'categorical' | 'link';

export type Props = {
  /**
   * `neutral` for a fact about the thing (a source type, a status), `categorical` for a topic or a
   * category, which is what design/10-foundations.md means by "a badge". `link` exists for the one
   * shipped chip that is blue and is not a decision to copy; see design/50-components.md §Badge.
   */
  tone?: Tone;
  children: ReactNode;
};

/**
 * A small inline pill that labels the thing beside it.
 *
 * The whole point of the component is that a call site chooses a tone and nothing else. Size, radius
 * and inset are not props, and there are no `--ds-badge-*` custom properties to reach past them with
 * (deliberately unlike Button, whose call sites legitimately vary in size): per-call-site geometry is
 * the drift this replaced. Six chips across the app had each landed on their own combination of
 * height, padding and radius, and two of them sat side by side in the same row of article metadata
 * at visibly different heights (PLT-6260).
 *
 * A badge is not a control. It has no press handler, no focus ring and no hover state, because
 * "labels a thing and never changes" is how the reader tells it from a chip they can click
 * (design/10-foundations.md, "A chip is either a badge or a control"). Put a `Link` inside it when
 * the label needs to be actionable, as the article "URL" chip does, and the affordance then belongs
 * visibly to the link rather than to the pill.
 *
 * Icons go in as children and are capped to the text size by the stylesheet, so asking for a bigger
 * icon cannot make a taller badge. That cap is specifically about an icon component put directly
 * inside the badge; it does not survive an inline `style`, an `<img>`, or a child that declares its
 * own leading (see the comment on the rule in Badge.module.css). Keep the children to a label and an
 * icon, which is all a badge is for, and the height holds.
 */
export function Badge(props: Props) {
  const { tone = 'neutral', children } = props;
  return <span className={clsx(styles.root, styles[`tone-${tone}`])}>{children}</span>;
}
